import { useForm, useFieldArray } from 'react-hook-form'
import { useTranslation } from 'react-i18next'
import { Dialog, FormItem, Button, Typogaphy } from 'vnetwork-ui'
import { bulkCreateSalesTargets } from '@/modules/Report/components/BusinessTargets/services'
import {
    SalesTargetCreate,
    SalesTarget,
    TargetScope,
} from '@/modules/Report/components/BusinessTargets/types'
import EmployeeSelect from '@/components/Form/Shared/EmployeeSelect'
import { useEffect } from 'react'
import { ModalProps } from '@/types/shared'
import ProductGroupSelect from '@/components/Form/Shared/ProductGroupSelect'
import { useMutationWithHandler } from '@/hooks/useMutationWithHandler'
import { QUERY_KEYS } from '@/constants/reactQueryKeys'
import { formatCurrency } from '@/utils/function'

export interface CreateTargetModalProps extends ModalProps {
    editTarget?: SalesTarget
}

interface FormData {
    employee_id: number[]
    target_scope: TargetScope
    target_year: number
    product_group_name: string | null
    breakdown_per_month: boolean
    total_amount: number
    monthly_targets: Array<{
        month: number
        amount: number
    }>
}

const CreateTargetModal = ({
    open,
    onClose,
    editTarget,
}: CreateTargetModalProps) => {
    const { t } = useTranslation()

    const { control, watch, handleSubmit, setValue, reset } = useForm<FormData>(
        {
            defaultValues: {
                employee_id: [editTarget?.employee_id || 0],
                target_scope: editTarget?.target_scope || 'month',
                target_year:
                    editTarget?.target_year || new Date().getFullYear(),
                product_group_name: editTarget?.product_group_name || null,
                breakdown_per_month:
                    editTarget?.target_scope === 'year' ? false : true,
                total_amount: editTarget?.target_amount || 0,
                monthly_targets: Array.from({ length: 12 }, (_, i) => ({
                    month: i + 1,
                    amount:
                        editTarget?.target_scope === 'year'
                            ? 0
                            : editTarget?.target_amount,
                })),
            },
        }
    )

    const { fields } = useFieldArray({
        control,
        name: 'monthly_targets',
    })

    const breakdownPerMonth = watch('breakdown_per_month')
    const totalAmount = watch('total_amount')
    const targetScope = watch('target_scope')

    // Auto-distribute total amount when breakdown is disabled
    useEffect(() => {
        if (!breakdownPerMonth && totalAmount > 0 && targetScope === 'month') {
            const amountPerMonth = Math.floor(totalAmount / 12)
            fields.forEach((_, index) => {
                setValue(`monthly_targets.${index}.amount`, amountPerMonth)
            })
        }
    }, [breakdownPerMonth, totalAmount, targetScope, fields, setValue])

    const { mutateAsync, isPending } = useMutationWithHandler(
        {
            mutationFn: bulkCreateSalesTargets,
        },
        {
            reactQueryKeys: [QUERY_KEYS.REPORTS_BUSINESS_TARGETS],
            onSuccess: () => {
                reset()
                onClose()
            },
        }
    )

    const onSubmit = async (data: FormData) => {
        // Build targets array for bulk create
        const targets: SalesTargetCreate[] = []

        data?.employee_id?.forEach((empId) => {
            if (data?.breakdown_per_month) {
                // Create 12 monthly targets
                data.monthly_targets?.forEach((monthData) => {
                    if (monthData.amount > 0) {
                        targets?.push({
                            employee_id: empId,
                            target_amount: monthData.amount,
                            target_scope: 'month',
                            target_year: data.target_year,
                            target_month: monthData.month,
                            product_group_name: data?.product_group_name,
                            is_active: true,
                        })
                    }
                })
            } else {
                // Create 1 yearly target
                targets?.push({
                    employee_id: empId,
                    target_amount: data.total_amount,
                    target_scope: 'year',
                    target_year: data.target_year,
                    product_group_name: data?.product_group_name,
                    is_active: true,
                })
            }
        })

        await mutateAsync({ targets })
    }

    const currentYear = new Date().getFullYear()
    const YEAR_OPTIONS = Array.from({ length: 10 }, (_, i) => ({
        label: `${currentYear - 5 + i}`,
        value: currentYear - 5 + i,
    }))

    const calculatedTotal = fields.reduce((sum, _, idx) => {
        const amount = watch(`monthly_targets.${idx}.amount`)
        return sum + (amount || 0)
    }, 0)

    return (
        <Dialog
            open={open}
            onClose={onClose}
            title={t('employees.performance.create_target')}
            maxWidth="lg"
        >
            <form
                onSubmit={handleSubmit(onSubmit)}
                className="grid grid-col-2 md:grid-cols-4 gap-4"
            >
                {/* Basic Info */}
                <div className="col-span-2 md:col-span-4">
                    <EmployeeSelect
                        control={control}
                        name="employee_id"
                        label={t('employee')}
                        multiple
                    />
                </div>
                <FormItem
                    className="col-span-2 md:col-span-4"
                    control={control}
                    name="target_year"
                    type="select"
                    label={t('year')}
                    options={YEAR_OPTIONS}
                    allowClear
                />
                <FormItem
                    control={control}
                    name="breakdown_per_month"
                    type="checkbox"
                    className="col-span-2 md:col-span-4 bg-blue-50"
                    label={t(
                        'employees.performance.breakdown_target_per_month'
                    )}
                />

                <div className="col-span-2 md:col-span-4">
                    <ProductGroupSelect
                        control={control}
                        name="product_group_name"
                        showLabel
                    />
                </div>
                {!breakdownPerMonth && (
                    <div className="col-span-2 md:col-span-4">
                        <FormItem
                            control={control}
                            name="total_amount"
                            type="number"
                            label={t(
                                'employees.performance.total_target_amount'
                            )}
                            placeholder={t(
                                'employees.performance.enter_total_target_amount'
                            )}
                        />
                    </div>
                )}

                {/* Monthly Breakdown - Show when breakdown is enabled AND monthly scope */}
                {breakdownPerMonth && (
                    <div className="space-y-2 col-span-2 md:col-span-4">
                        <div className="flex justify-between items-center">
                            <Typogaphy className="font-medium">
                                {t('employees.performance.monthly_targets')}
                            </Typogaphy>
                            <span className="text-sm text-gray-600">
                                {t('total')}: {formatCurrency(calculatedTotal)}
                            </span>
                        </div>

                        <div className="space-y-2 p-4 bg-gray-50 rounded">
                            <div className="grid grid-cols-1 md:grid-cols-3 lg:grid-cols-4 gap-3">
                                {fields.map((field, index) => (
                                    <div key={field.id} className="space-y-0.5">
                                        <Typogaphy
                                            size="small"
                                            className="font-medium text-gray-700"
                                        >
                                            {t('month')} {index + 1}
                                        </Typogaphy>
                                        <FormItem
                                            control={control}
                                            name={
                                                `monthly_targets.${index}.amount` as any
                                            }
                                            type="number"
                                            placeholder="0"
                                        />
                                    </div>
                                ))}
                            </div>
                        </div>
                    </div>
                )}

                {/* Actions */}
                <div className="flex justify-end gap-3 pt-4 border-t col-span-2 md:col-span-4">
                    <Button
                        type="button"
                        variant="outlined"
                        onClick={onClose}
                        disabled={isPending}
                    >
                        {t('cancel')}
                    </Button>
                    <Button
                        type="submit"
                        variant="contained"
                        loading={isPending}
                        disabled={isPending}
                    >
                        {t('employees.performance.create_target')}
                    </Button>
                </div>
            </form>
        </Dialog>
    )
}

export default CreateTargetModal
